import { useCallback, useEffect, useRef } from 'react'

import { RESERVATION_BOOKING_MODAL_CLASS_NAME } from '../../styles/constants'

const isFocusInsideAnyDialog = (node: Element | null) => Boolean(node?.closest('[role="dialog"]'))

const focusDialogElement = (dialog: HTMLElement | null) => {
	if (!dialog) {
		return
	}

	if (dialog.getAttribute('tabindex') !== '-1') {
		dialog.setAttribute('tabindex', '-1')
	}

	dialog.focus({ preventScroll: true })
}

const getBookingDialog = (container: HTMLElement | null) => {
	const fromWrapper = container?.querySelector<HTMLElement>('[role="dialog"]')
	if (fromWrapper) {
		return fromWrapper
	}

	// Styleguide may portal the dialog; className is applied to the booking modal root.
	return (
		document.querySelector<HTMLElement>(`[role="dialog"].${RESERVATION_BOOKING_MODAL_CLASS_NAME}`) ??
		document.querySelector<HTMLElement>(`.${RESERVATION_BOOKING_MODAL_CLASS_NAME} [role="dialog"]`) ??
		document.querySelector<HTMLElement>(`.${RESERVATION_BOOKING_MODAL_CLASS_NAME}`)
	)
}

/**
 * Keeps keyboard/AT focus inside the booking modal when focused controls unmount.
 * - `handoffFocusToDialog`: call before setState that removes the focused node
 * - `focusout` fallback: reclaim if focus would otherwise leave all dialogs
 */
const useReservationBookingModalFocus = (isOpen: boolean) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const isOpenRef = useRef(isOpen)
	isOpenRef.current = isOpen

	const handoffFocusToDialog = useCallback(() => {
		focusDialogElement(getBookingDialog(containerRef.current))
	}, [])

	useEffect(() => {
		if (!isOpen) {
			return undefined
		}

		const onFocusOut = (event: FocusEvent) => {
			if (!isOpenRef.current) {
				return
			}

			const dialog = getBookingDialog(containerRef.current)
			const leaving = event.target
			if (!dialog || !(leaving instanceof Element) || !dialog.contains(leaving)) {
				return
			}

			const next = event.relatedTarget
			if (next instanceof Element && isFocusInsideAnyDialog(next)) {
				return
			}

			focusDialogElement(dialog)
		}

		document.addEventListener('focusout', onFocusOut, true)

		return () => {
			document.removeEventListener('focusout', onFocusOut, true)
		}
	}, [isOpen])

	return { containerRef, handoffFocusToDialog }
}

export default useReservationBookingModalFocus
